// File: frontend/src/pages/ContactV2/ContactV2.js

import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import ContactForm from '../../components/form/ContactForm/ContactForm';
import { FiCalendar, FiMail, FiGlobe, FiMessageSquare, FiShield, FiUsers, FiActivity, FiUserPlus } from 'react-icons/fi';
import { ChevronRight } from 'lucide-react';
import styles from './ContactV2.module.css';

const ContactV2 = () => {
  const { t } = useLanguage();
  const scrollContainerRef = useRef(null);
  const [scrollIndicatorWidth, setScrollIndicatorWidth] = useState(0);
  const [scrollIndicatorPosition, setScrollIndicatorPosition] = useState(0);

  // Add this useEffect to handle the scrollbar indicator
  useEffect(() => {
    const updateScrollIndicator = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const scrollableWidth = container.scrollWidth;
        const visibleWidth = container.clientWidth;
        
        // Calculate the width of the indicator as a percentage of the visible area
        const widthPercentage = (visibleWidth / scrollableWidth) * 100;
        setScrollIndicatorWidth(widthPercentage);
        
        // Calculate the position of the indicator
        const scrollPercentage = (container.scrollLeft / (scrollableWidth - visibleWidth)) * 100;
        const maxTransform = 100 - widthPercentage;
        const position = scrollPercentage * (maxTransform / 100);
        setScrollIndicatorPosition(position);
      }
    };

    // Initial update
    updateScrollIndicator();
    
    // Add scroll event listener
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollIndicator);
    }
    
    // Add resize event listener
    window.addEventListener('resize', updateScrollIndicator);
    
    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener('scroll', updateScrollIndicator);
      }
      window.removeEventListener('resize', updateScrollIndicator);
    };
  }, []);

  const handleSubmit = (formData) => {
    // Handle form submission logic here
    console.log(formData);
  };

  const contactMethods = [
    {
      icon: <FiCalendar />,
      title: t('contact.methods.schedule_title', 'Schedule a Meeting'),
      description: t('contact.methods.schedule_desc', 'Book a time that works for you to discuss your needs with our team'),
      action: {
        text: t('schedule_on_calendly', 'Schedule on Calendly'),
        url: 'https://calendly.com/meet-with-allegory/30min',
        external: true
      }
    },
    {
      icon: <FiMail />,
      title: t('contact.methods.email_title', 'Email Us'),
      description: t('contact.methods.email_desc', 'Send us an email and we\'ll get back to you within 24 hours'),
      action: {
        text: 'contact@allegory.app',
        url: 'mailto:contact@allegory.app',
        external: true
      }
    },
    {
      icon: <FiGlobe />,
      title: t('contact.methods.regions_title', 'Service Regions'),
      description: t('contact.methods.regions_desc', 'Check if our services are available in your region'),
      action: {
        text: t('contact.view_supported_countries', 'View Supported Countries'),
        url: '/supported-countries',
        external: false
      }
    },
    {
      icon: <FiUsers />,
      title: t('contact.methods.join_title', 'Join Allegory'),
      description: t('contact.methods.join_desc', 'Become part of our growing community of innovators'),
      action: {
        text: t('contact.join_us', 'Join Us'),
        url: '/join',
        external: false
      }
    },
    {
      icon: <FiActivity />,
      title: t('contact.methods.status_title', 'System Status'),
      description: t('contact.methods.status_desc', 'Check our platform\'s operational status'),
      action: {
        text: t('contact.view_status', 'View Status'),
        url: '/status',
        external: false
      }
    },
    {
      icon: <FiUserPlus />,
      title: t('contact.methods.signup_title', 'Create Account'),
      description: t('contact.methods.signup_desc', 'Sign up for an Allegory account to get started'),
      action: {
        text: t('contact.sign_up', 'Sign Up'),
        url: '/sign-up',
        external: false
      }
    }
  ];

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = direction === 'right' ? 600 : -600;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contactContainer}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>{t('contact.get_in_touch', 'Get in Touch')}</h1>
          <div className={styles.titleUnderline}></div>
        </div>

        <section className={styles.contactMethodsSection}>
          <div className={styles.scrollableContainer} ref={scrollContainerRef}>
            {contactMethods.map((method, index) => (
              <div key={index} className={styles.methodCard}>
                <div className={styles.methodIcon}>{method.icon}</div>
                <h3 className={styles.methodTitle}>{method.title}</h3>
                <p className={styles.methodDescription}>{method.description}</p>
                {method.action.external ? (
                  <a 
                    href={method.action.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.methodButton}
                  >
                    {method.action.text}
                    <ChevronRight size={16} />
                  </a>
                ) : (
                  <Link to={method.action.url} className={styles.methodButton}>
                    {method.action.text}
                    <ChevronRight size={16} />
                  </Link>
                )}
              </div>
            ))}
          </div>
          
          {/* Add the scrollbar container and indicator */}
          <div className={styles.scrollbarContainer}>
            <div 
              className={styles.scrollbarIndicator} 
              style={{ 
                width: `${scrollIndicatorWidth}%`, 
                transform: `translateX(${scrollIndicatorPosition}%)` 
              }}
            ></div>
          </div>
        </section>

        <section className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('send_us_a_message')}</h2>
            <div className={styles.underline}></div>
          </div>
          
          <div className={styles.formContainer}>
            <div className={styles.formIntro}>
              <FiMessageSquare className={styles.formIcon} />
              <p className={styles.formDescription}>
                {t('contact.form_description', 'Fill out the form below and our team will get back to you as soon as possible. We value your feedback and inquiries.')}
              </p>
            </div>
            
            <ContactForm onSubmit={handleSubmit} className={styles.form} />
          </div>
        </section>

        <section className={styles.faqSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('contact.faq_title', 'Frequently Asked Questions')}</h2>
            <div className={styles.underline}></div>
          </div>
          
          <div className={styles.faqGrid}>
            <div className={styles.faqItem}>
              <h3>{t('contact.faq.q1', 'What services does Allegory offer?')}</h3>
              <p>{t('contact.faq.a1', 'Allegory provides innovative insurance technology solutions including AI-powered insurance management, risk assessment tools, and digital insurance platforms.')}</p>
            </div>
            
            <div className={styles.faqItem}>
              <h3>{t('contact.faq.q2', 'How can I become a partner?')}</h3>
              <p>{t('contact.faq.a2', 'To explore partnership opportunities, schedule a meeting with our team using the Calendly link above or send us a message through the contact form.')}</p>
            </div>
            
            <div className={styles.faqItem}>
              <h3>{t('contact.faq.q3', 'How can I get a quote?')}</h3>
              <p>
                  {t('contact.faq.a3', 'Visit our ')}
                  <Link to="/quote" className={styles.inlineLink}>{t('contact.quote_link', 'quote page')}</Link>
                  {t('contact.faq.a3_cont', ' and follow AI-1 guidance. If you look for a specific product, simply type its name and Allegory AI-1 will help you find the best rate.')}
              </p>
              </div>
                          
              <div className={styles.faqItem}>
              <h3>{t('contact.faq.q4', 'Where are your services available?')}</h3>
              <p>
                  {t('contact.faq.a4', 'Our digital insurance solutions are currently available in select regions.')} <Link to="/supported-countries" className={styles.inlineLink}>{t('contact.check_availability', 'Check availability in your area')}</Link>.
              </p>
            </div>
            
            <div className={styles.faqItem}>
              <div className={styles.faqItemHeader}>
                <FiShield className={styles.faqIcon} />
                <h3>{t('contact.faq.q5', 'Data Protection & Privacy')}</h3>
              </div>
              <p>{t('contact.faq.a5', 'Any information you provide during the quote process is securely stored in accordance with applicable data protection laws in your jurisdiction. Your data will never be sold or misused. You may request deletion of your quote information at any time. If you generate a quote now, you\'ll receive a notification when Allegory can legally operate in your jurisdiction with our own insurance products.')}</p>
            </div>
            
            <div className={styles.faqItem}>
              <h3>{t('contact.faq.q6', 'How quickly will I receive a response?')}</h3>
              <p>{t('contact.faq.a6', 'We aim to respond to all inquiries within 24 business hours. For urgent matters, we recommend scheduling a call through our Calendly link.')}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactV2;